import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService, EventBusService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { Router } from "@angular/router";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-upcoming-live-event",
  templateUrl: "fui-upcoming-live-event.component.html",
  standalone: false,
})
export class FuiUpcomingLiveEventComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() event: any = null;
  @Input() heroMode: boolean = false;
  @Input() viewingEventPage: boolean = false;
  public loading: boolean = false;
  public countdownDays: number = 0;
  public countdownHours: number = 0;
  public countdownMinutes: number = 0;
  public countdownSeconds: number = 0;
  public showCountdown: boolean = false;
  public isHappeningNow: boolean = false;
  private countdownInterval: any;
  public eventEnded: boolean = false;
  public requiresUpgrade: boolean = false;

  constructor(
    userService: UserService,
    public eventsService: EventsService,
    private eventBusService: EventBusService,
    private appService: AppService,
    private router: Router
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.event) {
      this.checkEventTiming();
      this.requiresUpgrade = this.event.fsp_exclusive && !this.user?.active_subscription;
      this.startCountdown();
    } else {
      const livestreams = this.getLivestreamCandidates();
      if (livestreams.length) {
        this.event = this.pickRelevantEvent(livestreams);
        this.initEvent();
      } else {
        this.loading = true;
        this.eventsService.getPublicEvents(50).subscribe({
          next: () => {
            this.loading = false;
            this.event = this.pickRelevantEvent(this.getLivestreamCandidates());
            this.initEvent();
          },
          error: () => {
            this.loading = false;
          },
        });
      }
    }
  }

  private initEvent() {
    if (this.event) {
      this.checkEventTiming();
      this.requiresUpgrade = this.event.fsp_exclusive && !this.user?.active_subscription;
      this.startCountdown();
    }
  }

  private getLivestreamCandidates(): any[] {
    return this.eventsService.cachedAllEvents.filter((e) => this.eventsService.isLivestreamEvent(e));
  }

  private pickRelevantEvent(events: any[]): any {
    if (!events.length) return null;
    const now = Date.now();
    const twelveHoursFromNow = now + 12 * 60 * 60 * 1000;
    for (const e of events) {
      const tz = e.timezone || "America/Chicago";
      const startTime = this.parseDatetimeInTimezone(e.start_datetime, tz).getTime();
      const endTime = this.parseDatetimeInTimezone(e.end_datetime || e.start_datetime, tz).getTime();
      if (endTime > now && startTime <= twelveHoursFromNow) return e;
    }
    return null;
  }

  private checkEventTiming() {
    if (!this.event?.start_datetime) return;
    const tz = this.event.timezone || "America/Chicago";
    const startTime = this.parseDatetimeInTimezone(this.event.start_datetime, tz).getTime();
    const endTime = this.event.end_datetime ? this.parseDatetimeInTimezone(this.event.end_datetime, tz).getTime() : startTime + 3600000;
    const now = Date.now();
    this.eventEnded = now > endTime;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  openLivestream(openTrailer: boolean = false) {
    const platform = this.eventsService.getLivestreamPlatform(this.event);
    const videoId = this.eventsService.getLivestreamVideoId(this.event);
    if (!platform || !videoId) return;
    this.eventBusService.emit(this.eventBusService.types.playVideo, {
      id: videoId,
      type: platform,
      isLiveEvent: this.isHappeningNow,
      // Hand the event along so the player can show the pre-show countdown without depending on
      // the events cache being warm.
      event: this.event,
      // "Watch Trailer" opens the player straight into the countdown's trailer modal.
      openTrailer,
    });
  }

  goToEvent() {
    this.router.navigate(["events", this.event.slug || this.event.id]);
  }

  upgrade() {
    this.appService.goToCheckout("/onboarding?subscribeOnly=true");
  }

  private startCountdown() {
    if (!this.event?.start_datetime) return;
    const tz = this.event.timezone || "America/Chicago";
    const startTime = this.parseDatetimeInTimezone(this.event.start_datetime, tz).getTime();
    const endTime = this.event.end_datetime ? this.parseDatetimeInTimezone(this.event.end_datetime, tz).getTime() : startTime + 3600000;

    const tick = () => {
      const now = Date.now();
      if (now >= startTime && now <= endTime) {
        this.showCountdown = false;
        this.isHappeningNow = true;
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        return;
      }
      if (now > endTime) {
        this.showCountdown = false;
        this.isHappeningNow = false;
        this.eventEnded = true;
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        return;
      }
      this.showCountdown = true;
      this.isHappeningNow = false;
      const diff = startTime - now;
      this.countdownDays = Math.floor(diff / 86400000);
      this.countdownHours = Math.floor((diff % 86400000) / 3600000);
      this.countdownMinutes = Math.floor((diff % 3600000) / 60000);
      this.countdownSeconds = Math.floor((diff % 60000) / 1000);
    };

    tick();
    this.countdownInterval = setInterval(tick, 1000);
  }

  private parseDatetimeInTimezone(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }
}
